import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';

const SETUP2_TABLE = 'setup2';
const TERMS_PREFIX = 'terms_and_condition';

const termsIndex = (functionname) => {
  const match = /^terms_and_condition(\d+)$/.exec(functionname ?? '');
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

/**
 * Fetch the org's terms and conditions lines from `setup2`, ordered by the
 * numeric suffix of `functionname` (terms_and_condition1, 2, ...).
 * Returns an array of `functionvalue` strings.
 */
export const fetchTerms = async () => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const { data, error } = await supabase
    .from(SETUP2_TABLE)
    .select('*')
    .eq('org_id', orgId);

  if (error) throw error;

  return (data || [])
    .filter((row) => typeof row.functionname === 'string' && row.functionname.startsWith(TERMS_PREFIX))
    .sort((a, b) => termsIndex(a.functionname) - termsIndex(b.functionname))
    .map((row) => (row.functionvalue == null ? '' : String(row.functionvalue)));
};

/**
 * Replace the org's terms and conditions lines in `setup2`.
 *
 * Uses a delete-then-insert strategy: empty lines are skipped, and the kept
 * lines are re-numbered in array order as terms_and_condition1..N. This keeps
 * ordering stable and avoids relying on a unique constraint.
 *
 * Returns the saved (trimmed, non-empty) lines.
 */
export const saveTerms = async (lines) => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const kept = (Array.isArray(lines) ? lines : [])
    .map((line) => (line == null ? '' : String(line).trim()))
    .filter((line) => line !== '');

  const { error: deleteError } = await supabase
    .from(SETUP2_TABLE)
    .delete()
    .eq('org_id', orgId)
    .like('functionname', `${TERMS_PREFIX}%`);

  if (deleteError) throw deleteError;

  if (kept.length > 0) {
    const rows = kept.map((value, index) => ({
      org_id: orgId,
      functionname: `${TERMS_PREFIX}${index + 1}`,
      functionvalue: value,
    }));

    const { error: insertError } = await supabase.from(SETUP2_TABLE).insert(rows);
    if (insertError) throw insertError;
  }

  return kept;
};
